import type { Socket } from 'socket.io-client';

export const MAX_SERVER_DISCONNECT_RECONNECTS = 3;

const SERVER_DISCONNECT_REASON: Socket.DisconnectReason =
  'io server disconnect';

type TRetryHintError = Error & { data?: { 'retry-ms'?: number } };

type TReconnectableSocket = Pick<Socket, 'connect'> & {
  on: {
    (
      eventName: 'disconnect',
      listener: (reason: Socket.DisconnectReason) => void,
    ): unknown;
    (eventName: 'connect', listener: () => void): unknown;
    (
      eventName: 'connect_error',
      listener: (connectionError: TRetryHintError) => void,
    ): unknown;
  };
};

/**
 * Socket.IO does not reconnect by itself after the server closes a socket. The
 * backend closes every socket of a user who loses a company, so a user who
 * keeps another company needs a new connection to get live updates again. A
 * rejected handshake (inactive user, revoked token) ends in `connect_error`,
 * which does not retry. The attempts are capped per connection: a successful
 * connect resets the count.
 */
export const reconnectAfterServerDisconnect = (
  socket: TReconnectableSocket,
): void => {
  let attemptCount = 0;

  socket.on('connect', () => {
    attemptCount = 0;
  });

  socket.on('disconnect', (reason) => {
    if (reason !== SERVER_DISCONNECT_REASON) {
      return;
    }

    if (attemptCount >= MAX_SERVER_DISCONNECT_RECONNECTS) {
      return;
    }

    attemptCount += 1;
    socket.connect();
  });
};

/**
 * The backend refuses a handshake with a `retry-ms` hint when the refusal is
 * temporary (rate limit, failed access lookup). The refusal ends in
 * `connect_error`, which does not retry, so this connects again after the
 * hint. A refusal without the hint (inactive user, revoked token) stays final.
 * Call the returned function on cleanup, so that a pending retry does not
 * reopen a closed socket.
 */
export const reconnectAfterRetryHint = (
  socket: TReconnectableSocket,
): (() => void) => {
  const pendingRetry: { timeoutId?: ReturnType<typeof setTimeout> } = {};

  socket.on('connect_error', (connectionError) => {
    const retryMs = connectionError.data?.['retry-ms'];

    if (typeof retryMs !== 'number') {
      return;
    }

    clearTimeout(pendingRetry.timeoutId);
    Object.assign(pendingRetry, {
      timeoutId: setTimeout(() => {
        socket.connect();
      }, retryMs),
    });
  });

  return () => {
    clearTimeout(pendingRetry.timeoutId);
  };
};
